/**
 * Surface hierarchy — the single referable contract for elevation roles.
 *
 * ─── The ladder ─────────────────────────────────────────────────────────
 * Eight levels (1–8). Values live in `app/styles/surfaces.css` (neutral
 * base), re-pointed for the app's one theme in
 * `app/styles/themes/theme-default.css`:
 *
 *   light: L1 = the brand's soft page fill; L2+ = white paper. Surfaces
 *          are told apart by the page/paper step and their hairline border,
 *          not by a colour per level; resting surfaces take a 1px seat
 *          shadow and floating ones (4+) one soft drop.
 *   dark:  L1 = the dark ground, L2–L3 the lifted dark fill, L4+ one step
 *          brighter so a menu over a card still separates.
 *
 * The ladder is MONOTONIC: a level-N surface is never darker (light mode)
 * or dimmer (dark mode) than the level below it. A container must never
 * paint darker than content sitting on top of it.
 *
 * ─── Semantic token wiring (tokens.css) ─────────────────────────────────
 *   --background = page substrate  (level 1)
 *   --card       = var(--surface-2) — a FIXED level-2 paint
 *   --popover    = var(--surface-3) — a FIXED level-3 paint
 *
 * `bg-card` / `bg-popover` do NOT step with nesting. That is exactly how
 * "nested rectangle exactly the same color as its parent" violations
 * happen: `bg-card` inside a level-2 card repaints the parent's own color.
 * Inside anything already elevated, route through `<Elevated>` /
 * `getSurfaceClasses()` instead of the fixed semantic tokens.
 *
 * ─── Roles ──────────────────────────────────────────────────────────────
 * Two kinds of container, and they are styled oppositely:
 *
 * RAISED — sits ON the substrate, lifts one or more steps, casts a shadow.
 *   Use `<Elevated offset={…}>` (or `getSurfaceClasses(resolveSurfaceLevel(
 *   useSurface(), offset))` when wrapping a third-party root). The resolved
 *   level is re-provided via SurfaceProvider so descendants keep stepping.
 *
 * RECESSED — cut INTO the current surface: control tracks, wells, code
 *   blocks, empty-state zones, muted list headers. Paints `bg-muted` (or a
 *   `bg-muted/50` tint for a softer well), NEVER carries a drop shadow,
 *   and never re-provides a level (content inside a well still belongs to
 *   the host surface).
 *
 * Never express hierarchy by painting a CHILD darker than its parent with
 * an opaque fill (that inverts the ladder); recessed wells are the one
 * sanctioned "darker" treatment and must read as engraved (borderless or
 * hairline-bordered, shadowless), not as a floating panel.
 *
 * ─── Matching the host surface ──────────────────────────────────────────
 * A descendant that must repaint EXACTLY its host's surface (sticky list
 * headers, flush footers over scrolling content) uses `bg-surface-current`
 * (optionally with alpha: `bg-surface-current/95`). Every element that
 * stamps `data-surface-level` publishes `--surface-current` for its
 * subtree (see surfaces.css) — never guess with `bg-background`/`bg-card`,
 * which only match at one specific depth.
 *
 * ─── Sibling rule ───────────────────────────────────────────────────────
 * Siblings at the same conceptual depth share one level. A child that
 * merely groups content (padding/gap wrapper) paints NOTHING — only
 * containers that mean "this is a distinct panel" spend a level. Levels
 * are a scarce resource: a config page should use substrate (1) → section
 * card (2) → nested panel (3) and stop; overlays take it from there.
 */

import type { SurfaceLevel } from "./surface-level";

/**
 * Canonical offsets, matching the shipped primitives. If you build a new
 * surface-bearing component, pick the row that matches its role instead of
 * inventing a new offset:
 *
 *   raised      +1 — Card, SectionPanel, any nested raised panel
 *   overlay     +2 — Popover, DropdownMenu, Select, Combobox, HoverCard
 *   submenu     +1 — a menu's submenu, relative to the parent menu
 *   modal       +3 — Dialog, Sheet (block interaction underneath)
 */
export const SURFACE_OFFSETS = {
	raised: 1,
	overlay: 2,
	submenu: 1,
	modal: 3,
} as const;

/**
 * Pinned levels for components whose depth must NOT vary with nesting —
 * they always sit at the top of the stack regardless of trigger location.
 *
 *   tooltip 6 — reads above any dialog, below toasts
 *   toast   8 — nothing renders above a toast
 */
export const SURFACE_PINNED_LEVELS = {
	tooltip: 6 as SurfaceLevel,
	toast: 8 as SurfaceLevel,
} as const;

/**
 * Identity shadow per role — the shadow recipe the component has at BASE
 * depth (substrate 1 + its offset). Per the Fluid Functionalism rule, the
 * BACKGROUND steps with the substrate but the SHADOW stays the component's
 * identity: "the shadow doesn't change, so a popover still reads as a
 * popover three layers down." Pass as `getSurfaceClasses(level,
 * { shadowLevel: SURFACE_IDENTITY_SHADOWS.overlay })` in overlay
 * primitives. The generic `<Elevated/>` keeps the spec default
 * (`shadowLevel ?? level`) — only NAMED roles pin their shadow.
 */
export const SURFACE_IDENTITY_SHADOWS = {
	raised: 2 as SurfaceLevel, // 1 + raised(+1)  — Card, panels
	overlay: 3 as SurfaceLevel, // 1 + overlay(+2) — Popover, menus, Select
	modal: 4 as SurfaceLevel, // 1 + modal(+3)   — Dialog, Sheet
	// Toast pins bg level 8 (nothing renders above it) but keeps a QUIET
	// floating-element shadow — a level-8 shadow under every toast would
	// read as maximum drama for a transient notice. The one sanctioned
	// case of shadowLevel far below level.
	toast: 4 as SurfaceLevel,
} as const;

/**
 * Shadow weight by component WIDTH — a size heuristic for one-off floating
 * panels that don't match a named role above. A physically larger surface
 * plausibly sits "heavier", so its shadow scales with footprint:
 *
 *   under ~400px wide          → shadowLevel 3 (dropdown/popover class)
 *   ~400–800px                 → shadowLevel 5 (dialog class)
 *   over ~800px / fullscreen   → shadowLevel 7 (hero modal class)
 *
 * Constraint (dev-enforced in getSurfaceClasses/<Elevated>): never let
 * `shadowLevel` exceed `level` by more than 2 — a shadow much heavier
 * than its surface reads as detached.
 */
export const SURFACE_SHADOW_BY_WIDTH = {
	narrow: 3 as SurfaceLevel,
	medium: 5 as SurfaceLevel,
	wide: 7 as SurfaceLevel,
} as const;

/**
 * Recessed-well paint. A well is engraved into the host surface, not
 * lifted off it: derived fill, no drop shadow, no SurfaceProvider re-issue.
 *
 * `bg-surface-well` is the DERIVED well color (`--surface-well` in
 * surfaces.css): the host's own `--surface-current` darkened by a fixed
 * ink wash, re-resolved at every `[data-surface-level]`. A well is always
 * cut into the surface it actually sits on — never the fixed `bg-muted`
 * token, which paints the same absolute color at every depth and makes a
 * well inside an elevated popover repeat the page/sidebar track color.
 *
 * `well` is the standard track/inset; `wellSoft` is a lighter tint for
 * large zones (empty states, drop targets) where the full wash reads too
 * heavy.
 */
export const SURFACE_RECESSED_CLASSES = {
	well: "bg-surface-well",
	wellSoft: "bg-surface-well/50",
} as const;
